import { useAppStateSelector } from '@src/providers/AppStateProvider';
import {
  BALANCE_DELTA,
  Dd,
  Dl,
  Dt,
  Flex,
  InlineFormattedNumber,
  Text,
  Tooltip,
  getRepresentativeOptionSecurityKey,
  useCurrency,
  useMarketAccountsContext,
  useObservableValue,
  useRefRate,
  useSecuritiesContext,
  useSecurity,
  useSparklineData,
  useSubscription,
  type BoxProps,
  type OptionBalance,
  type RequestStream,
} from '@talos/kyoko';
import Big from 'big.js';
import { transparentize } from 'polished';
import { useCallback, useMemo, type ReactNode } from 'react';
import { map } from 'rxjs';
import styled, { css } from 'styled-components';
import type { BalancesFilter } from '../../../../types';
import { selectOptionFilter } from './Filter/selectors';
import { OPTION_BALANCES_TOOLTIPS } from './tooltips';

export function OptionBalances({ panelID }: { panelID: string }) {
  const optionFilter = useAppStateSelector(state => selectOptionFilter(state, panelID));
  const { marketAccountsByName } = useMarketAccountsContext();
  const marketAccount = optionFilter.marketAccountName
    ? marketAccountsByName.get(optionFilter.marketAccountName)
    : undefined;

  const { representativeOptionSecurityByKey } = useSecuritiesContext();
  const representativeSecurity = useMemo(() => {
    return representativeOptionSecurityByKey.get(
      getRepresentativeOptionSecurityKey(optionFilter.currency, [optionFilter.market], optionFilter.underlyingCode)
    );
  }, [representativeOptionSecurityByKey, optionFilter]);
  const spotSymbol =
    representativeSecurity?.UnderlyingSymbol ??
    `${optionFilter.currency}-${representativeSecurity?.UnderlyingQuoteCurrency ?? 'USD'}`;
  const filterCurrency = useCurrency(optionFilter.currency);
  const spotSecurity = useSecurity(spotSymbol);

  const sparklinesObservable = useSparklineData(spotSymbol);
  const refRatesObservable = useRefRate(spotSymbol);
  const latestSparkline = useObservableValue(() => sparklinesObservable, [sparklinesObservable]);
  const latestRefRate = useObservableValue(() => refRatesObservable, [refRatesObservable]);
  const percentChange = Big(latestSparkline?.PercentChange ?? 0);

  const balanceRequest: RequestStream & BalancesFilter = useMemo(() => {
    const MarketAccounts = marketAccount?.Name ? [marketAccount.Name] : undefined;
    return {
      name: BALANCE_DELTA,
      tag: 'OptionBalances',
      Markets: [optionFilter.market],
      MarketAccounts: MarketAccounts,
      Currencies: [representativeSecurity?.SettlementCurrency ?? optionFilter.currency],
    };
  }, [marketAccount, optionFilter, representativeSecurity]);

  const { data } = useSubscription<Partial<OptionBalance>>(balanceRequest);
  const balancesData = useObservableValue(() => data.pipe(map(json => json.data.at(0))), [data]) || fallbackBalance;
  const balanceCurrency = useCurrency(balancesData.Currency) || filterCurrency;

  return (
    <BalancesContainer flexDirection="row" columnGap="spacing32" px="spacing12" py="spacing8">
      <Flex flexDirection="row" columnGap="spacing32" rowGap="spacing8" flexWrap="wrap">
        <Flex flexDirection="row" columnGap="spacing32">
          <BalanceItem title="Margin Asset">
            <Text color="colorTextImportant">{balanceCurrency?.Description}</Text>
          </BalanceItem>

          <BalanceItem title="Market Account">
            <Text color="colorTextImportant">{marketAccount?.DisplayName || optionFilter.marketAccountName}</Text>
          </BalanceItem>

          <BalanceItem title="Equity" tooltip={OPTION_BALANCES_TOOLTIPS.Equity}>
            <InlineFormattedNumber
              number={balancesData.Equity}
              currency={balancesData.Currency}
              increment={balanceCurrency?.DefaultIncrement}
            />
          </BalanceItem>

          <BalanceItem title="Available Balance" tooltip={OPTION_BALANCES_TOOLTIPS.AvailableMargin}>
            <InlineFormattedNumber
              number={balancesData.AvailableMargin}
              currency={balancesData.Currency}
              increment={balanceCurrency?.DefaultIncrement}
            />
          </BalanceItem>
        </Flex>

        <Flex flexDirection="row" columnGap="spacing32">
          <BalanceItem title="Initial Margin" tooltip={OPTION_BALANCES_TOOLTIPS.InitialMargin}>
            <InlineFormattedNumber
              number={balancesData.InitialMargin}
              currency={balancesData.Currency}
              increment={balanceCurrency?.DefaultIncrement}
            />
          </BalanceItem>

          <BalanceItem title="Maintenance Margin" tooltip={OPTION_BALANCES_TOOLTIPS.MaintenanceMargin}>
            <InlineFormattedNumber
              number={balancesData.MaintenanceMargin}
              currency={balancesData.Currency}
              increment={balanceCurrency?.DefaultIncrement}
            />
          </BalanceItem>
        </Flex>
      </Flex>
      {spotSecurity && latestRefRate?.currentPrice && (
        <BalanceItem title={`${spotSecurity.BaseCurrency} Spot Price`} ml="auto" textAlign="right">
          <InlineFormattedNumber
            number={latestRefRate.currentPrice}
            currency={spotSecurity.QuoteCurrency}
            increment={spotSecurity.DefaultPriceIncrement}
          />
          <Text
            weight="fontWeightMedium"
            color={percentChange.gt(0) ? 'colorTextPositive' : percentChange.lt(0) ? 'colorTextNegative' : undefined}
            data-testid="percentage-change"
          >
            {percentChange.gt(0) ? '+' : ''}
            {percentChange.toFixed(2)}%
          </Text>
        </BalanceItem>
      )}
    </BalancesContainer>
  );
}

function BalanceItem({
  title,
  children,
  tooltip,
  ...boxProps
}: { title: string; tooltip?: string; children: ReactNode } & Pick<BoxProps, 'ml' | 'textAlign'>) {
  const MaybeTooltip = useCallback(
    ({ children }: { children: ReactNode }) => {
      if (tooltip) {
        return (
          <Tooltip
            targetStyle={{ width: 'fit-content' }}
            showUnderline
            tooltip={<Text whiteSpace="pre-line">{tooltip}</Text>}
          >
            {children}
          </Tooltip>
        );
      }
      return <>{children}</>;
    },
    [tooltip]
  );

  return (
    <Dl display="flex" flexDirection="column" gap="spacing4" {...boxProps}>
      <MaybeTooltip>
        <Dt fontSize="fontSizeXs" textTransform="uppercase" color="gray.080">
          {title}
        </Dt>
      </MaybeTooltip>
      <Dd display="flex" mt={0} gap="spacing4" minHeight="spacing16">
        {children}
      </Dd>
    </Dl>
  );
}

const BalancesContainer = styled(Flex)`
  ${({ theme }) => css`
    background-color: ${transparentize(1 - 0.12, theme.colors.gray['000'])};
  `}
`;

const fallbackBalance: Partial<OptionBalance> = {};
